import { useEffect, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  useMediaQuery,
  Link,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Grid,
  Theme,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useActivateCardStore, useBlockCardStore, useReplaceCardStore, useReprintCardStore, useUnblockCardStore } from '@lib/store';
import { CardEntity, CustomerEntity, AccountEntity } from '@shared/api';
import { PermissionsUtils } from '@shared/utils';
import { PermissionLevel, PermissionType } from '@shared/types';

interface Props {
  hideCardColumns?: boolean;
  data: CardEntity[];
  customerData: CustomerEntity;
  accountData: AccountEntity;
  onRefresh?: () => void;
}

const useStyles = makeStyles<Theme>(() => ({
  dialogHeaderTitle: {
    fontSize: '20px',
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  action: {
    minWidth: '140px',
  },
  confirmActions: {
    position: 'relative',
  },
  dialogLoading: {
    left: '50%',
    marginLeft: -12,
    marginTop: -12,
    position: 'absolute',
    top: '50%',
  },
}));

interface AllowedActionsIndex {
  [key: string]: boolean;
}

interface CardsAllowedActionsIndex {
  [key: string]: AllowedActionsIndex;
}

const HAS_ROW_ACTIONS = '$HAS_ROW_ACTIONS';
const HAS_ROW_ACTION_ACTIVATE = '$HAS_ROW_ACTION_ACTIVATE';
const HAS_ROW_ACTION_REPLACE = '$HAS_ROW_ACTION_REPLACE';
const HAS_ROW_ACTION_REPRINT = '$HAS_ROW_ACTION_REPRINT';
const HAS_ROW_ACTION_BLOCK = '$HAS_ROW_ACTION_BLOCK';

function cardIsBlocked(card: CardEntity): boolean {
  if (card.blockCode === 'U') return false;
  return !!card.blockCode && card.blockCode !== '-';
}

function cardIsActive(card: CardEntity): boolean {
  return card.status === 'Active';
}

// function cardActionIsAllowed(card: CardEntity, index: CardsAllowedActionsIndex, action: string): boolean {
//   if (!card || !index) return false;
//   const item = index[card.cardId];
//   if (!item) return false;
//   return item[action];
// }

// function canCardActivate(card: CardEntity, index: CardsAllowedActionsIndex): boolean {
//   return cardActionIsAllowed(card, index, 'ACTIVATE');
// }

// function canCardReplace(card: CardEntity, index: CardsAllowedActionsIndex): boolean {
//   return cardActionIsAllowed(card, index, 'REPLACEMENT');
// }

// function canCardReprint(card: CardEntity, index: CardsAllowedActionsIndex): boolean {
//   return cardActionIsAllowed(card, index, 'PINREPRINT');
// }

// function canCardBlock(card: CardEntity, index: CardsAllowedActionsIndex): boolean {
//   return cardActionIsAllowed(card, index, 'CARDBLOCK');
// }

const defaultDialogResponse = 'Are you sure you want to proceed?';

export const CardsList = ({ data, customerData, accountData, onRefresh }: Props) => {
  const classes = useStyles();

  const { loading: loadingActivateCard, activateCard, clear: clearActivatedCard } = useActivateCardStore();
  const { loading: loadingReplaceCard, replaceCard, clear: clearReplacedCard } = useReplaceCardStore();
  const { loading: loadingReprintCard, reprintPin, clear: clearReprintedCard } = useReprintCardStore();
  const { loading: loadingBlockCard, blockCard, clear: clearBlockedCard } = useBlockCardStore();
  const { loading: loadingUnblockCard, unblockCard, clear: clearUnblockedCard } = useUnblockCardStore();
  const confirmationLoading = loadingActivateCard || loadingReplaceCard || loadingReprintCard || loadingBlockCard || loadingUnblockCard;

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [actionsDialogOpen, setActionsDialogOpen] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(true);
  const [selectedCard, setSelectedCard] = useState<CardEntity>();
  const [dialogResponse, setDialogResponse] = useState<string>(defaultDialogResponse);
  const [selectedCardAction, setSelectedCardAction] = useState<string>();
  const [selectedCardId, setSelectedCardId] = useState<string>();
  // Card Anchors to sort menu item index issue
  const [cardAnchors, setCardAnchors] = useState<Array<Element | null>>(data.map(() => null));

  const largeScreen = useMediaQuery('(min-width:800px)');

  const hasActivatePermission = PermissionsUtils.hasPermission(PermissionType.CARDS, PermissionLevel.ACTIVATE);
  const hasReplacePermission = PermissionsUtils.hasPermission(PermissionType.CARDS, PermissionLevel.REPLACE);
  const hasReprintPermission = PermissionsUtils.hasPermission(PermissionType.CARDS, PermissionLevel.PIN_REPRINT);
  const hasBlockPermission = PermissionsUtils.hasPermission(PermissionType.CARDS, PermissionLevel.BLOCK);

  const cardsAllowedActionsIndex = useMemo<CardsAllowedActionsIndex>(() => {
    return data
      ? data.reduce((acc, row) => {
          acc[row.cardId] = row.allowedActions
            ? row.allowedActions.reduce((acc, action) => {
                acc[action] = true;
                return acc;
              }, {} as AllowedActionsIndex)
            : ({} as AllowedActionsIndex);
          acc[row.cardId][HAS_ROW_ACTION_ACTIVATE] =
            hasActivatePermission && !cardIsActive(row) && acc[row.cardId]['ACTIVATE'] && row.blockCode === 'U';
          acc[row.cardId][HAS_ROW_ACTION_REPLACE] = hasReplacePermission && acc[row.cardId]['REPLACEMENT'];
          acc[row.cardId][HAS_ROW_ACTION_REPRINT] = hasReprintPermission && acc[row.cardId]['PINREPRINT'];
          acc[row.cardId][HAS_ROW_ACTION_BLOCK] = hasBlockPermission && acc[row.cardId]['CARDBLOCK'];
          acc[row.cardId][HAS_ROW_ACTIONS] =
            acc[row.cardId][HAS_ROW_ACTION_ACTIVATE] ||
            acc[row.cardId][HAS_ROW_ACTION_REPLACE] ||
            acc[row.cardId][HAS_ROW_ACTION_REPRINT] ||
            acc[row.cardId][HAS_ROW_ACTION_BLOCK];
          return acc;
        }, {} as CardsAllowedActionsIndex)
      : ({} as CardsAllowedActionsIndex);
  }, [data, hasActivatePermission, hasBlockPermission, hasReplacePermission, hasReprintPermission]);

  useEffect(() => {
    if (selectedCard) {
      const newSelectedCard = data ? data.filter((item) => item.cardId === selectedCard.cardId)[0] : undefined;
      setSelectedCard(newSelectedCard);
    }
  }, [setSelectedCard, data]);

  // Open Card Menu Item
  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, idx: number) => {
    setCardAnchors((curAnchors) => {
      const anchors = curAnchors.slice();
      anchors.splice(idx, 1, e.currentTarget);
      return anchors;
    });
  };

  // Close Card Menu Item
  const handleClose = (idx: number) => {
    setCardAnchors((curAnchors) => {
      const anchors = curAnchors.slice();
      anchors.splice(idx, 1, null);
      return anchors;
    });
    setShowConfirm(true);
  };

  const callOnConfirm = async () => {
    if (!selectedCardId) return;
    switch (selectedCardAction) {
      case 'activate': {
        const result = await activateCard(selectedCardAction);
        if (result.data.length) {
          onRefresh?.();
          setDialogResponse('Success');
          setShowConfirm(false);
          clearActivatedCard();
        }
        if (result.error) setDialogResponse(result.error);
        break;
      }
      case 'replace': {
        const result = await replaceCard(selectedCardId);
        if (result.data.length) {
          onRefresh?.();
          setDialogResponse('Your request is pending approval - Insignia will get back to you on your request');
          setShowConfirm(false);
          clearReplacedCard();
        }
        if (result.error) setDialogResponse(result.error);
        break;
      }
      case 'reprint': {
        const result = await reprintPin(selectedCardId);
        if (result.data.length) {
          onRefresh?.();
          setDialogResponse('Your request is pending approval - Insignia will get back to you on your request');
          setShowConfirm(false);
          clearReprintedCard();
        }
        if (result.error) setDialogResponse(result.error);
        break;
      }
      case 'block': {
        const result = await blockCard(selectedCardId);
        if (result.data.length) {
          onRefresh?.();
          setDialogResponse('Success');
          setShowConfirm(false);
          clearBlockedCard();
        }
        if (result.error) setDialogResponse(result.error);
        break;
      }
      case 'unblock': {
        const result = await unblockCard(selectedCardId);
        if (result.data.length) {
          onRefresh?.();
          setDialogResponse('Success');
          setShowConfirm(false);
          clearUnblockedCard();
        }
        if (result.error) setDialogResponse(result.error);
        break;
      }
    }
  };

  const getSelectedCardDetails = (card: string) => {
    const cardClicked = card;
    data.forEach((dataItem) => {
      if (dataItem.cardId === cardClicked) {
        setSelectedCard(dataItem);
      }
    });
    setDetailsDialogOpen(true);
  };

  return (
    <>
      {largeScreen ? (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='center'>
                Card Number
              </TableCell>

              <TableCell style={{ width: 150 }} align='center'>
                Embossing Name
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Card Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Card Status
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow key={row.cardId}>
                <TableCell>
                  <Button variant='text' color='primary' onClick={() => getSelectedCardDetails(row.cardId)}>
                    **** **** **** {row.lastFourDigits}
                  </Button>
                </TableCell>

                <TableCell align='center'>{row.embossName}</TableCell>
                <TableCell align='center'>{row.cardType}</TableCell>
                <TableCell align='center'>{row.status}</TableCell>
                <TableCell align='center'>
                  {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTIONS] ? (
                    <IconButton onClick={(e) => handleClick(e, idx)} size='large'>
                      <MoreVertIcon />
                    </IconButton>
                  ) : null}

                  <Menu anchorEl={cardAnchors[idx]} open={Boolean(cardAnchors[idx])} onClick={() => handleClose(idx)}>
                    {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_ACTIVATE] && (
                      <MenuItem
                        onClick={() => {
                          setSelectedCardId(row.cardId);
                          setSelectedCardAction('activate');
                          setDialogResponse(defaultDialogResponse);
                          setActionsDialogOpen(true);
                        }}
                      >
                        Activate Card
                      </MenuItem>
                    )}
                    {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_REPLACE] && (
                      <MenuItem
                        onClick={() => {
                          setSelectedCardId(row.cardId);
                          setSelectedCardAction('replace');
                          setDialogResponse(defaultDialogResponse);
                          setActionsDialogOpen(true);
                        }}
                      >
                        Replace Card
                      </MenuItem>
                    )}
                    {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_REPRINT] && (
                      <MenuItem
                        onClick={() => {
                          setSelectedCardId(row.cardId);
                          setSelectedCardAction('reprint');
                          setDialogResponse(defaultDialogResponse);
                          setActionsDialogOpen(true);
                        }}
                      >
                        Reprint Pin
                      </MenuItem>
                    )}
                    {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_BLOCK] && (
                      <MenuItem
                        onClick={() => {
                          setSelectedCardId(row.cardId);
                          setSelectedCardAction(!cardIsBlocked(row) ? 'block' : 'unblock');
                          setDialogResponse(defaultDialogResponse);
                          setActionsDialogOpen(true);
                        }}
                      >
                        {!cardIsBlocked(row) ? 'Block Card' : 'Unblock Card'}
                      </MenuItem>
                    )}
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 100 }} align='center'>
                Card Id
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Last Four Digits
              </TableCell>
              <TableCell style={{ width: 150 }} align='center'>
                Customer Code
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Card Type
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Card Status
              </TableCell>
              <TableCell style={{ width: 100 }} align='center'>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data &&
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                <TableRow key={row.cardId}>
                  <TableCell align='center'>
                    <Link onClick={() => getSelectedCardDetails(row.cardId)}>**** **** **** {row.lastFourDigits}</Link>
                  </TableCell>

                  <TableCell align='center'>{row.lastFourDigits}</TableCell>
                  <TableCell align='center'>{row.custCode}</TableCell>
                  <TableCell align='center'>{row.cardType}</TableCell>
                  <TableCell align='center'>{row.status}</TableCell>
                  <TableCell align='center'>
                    {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTIONS] ? (
                      <IconButton onClick={(e) => handleClick(e, idx)} size='large'>
                        <MoreVertIcon />
                      </IconButton>
                    ) : null}
                    <Menu anchorEl={cardAnchors[idx]} open={Boolean(cardAnchors[idx])} onClick={() => handleClose(idx)}>
                      {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_ACTIVATE] && (
                        <MenuItem
                          onClick={() => {
                            setSelectedCardId(row.cardId);
                            setSelectedCardAction('activate');
                            setDialogResponse(defaultDialogResponse);
                            setActionsDialogOpen(true);
                          }}
                        >
                          Activate Card
                        </MenuItem>
                      )}
                      {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_REPLACE] && (
                        <MenuItem
                          onClick={() => {
                            setSelectedCardId(row.cardId);
                            setSelectedCardAction('replace');
                            setDialogResponse(defaultDialogResponse);
                            setActionsDialogOpen(true);
                          }}
                        >
                          Replace Card
                        </MenuItem>
                      )}
                      {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_REPRINT] && (
                        <MenuItem
                          onClick={() => {
                            setSelectedCardId(row.cardId);
                            setSelectedCardAction('reprint');
                            setDialogResponse(defaultDialogResponse);
                            setActionsDialogOpen(true);
                          }}
                        >
                          Reprint Pin
                        </MenuItem>
                      )}
                      {cardsAllowedActionsIndex[row.cardId][HAS_ROW_ACTION_BLOCK] && (
                        <MenuItem
                          onClick={() => {
                            setSelectedCardId(row.cardId);
                            setSelectedCardAction(!cardIsBlocked(row) ? 'block' : 'unblock');
                            setDialogResponse(defaultDialogResponse);
                            setActionsDialogOpen(true);
                          }}
                        >
                          {!cardIsBlocked(row) ? 'Block Card' : 'Unblock Card'}
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setPage(0);
          setRowsPerPage(+e.target.value);
        }}
      />
      {selectedCard && (
        <Dialog disableEscapeKeyDown fullWidth={true} maxWidth='md' aria-labelledby='confirmation-dialog-title' open={detailsDialogOpen}>
          <DialogTitle id='confirmation-dialog-title' className={classes.dialogHeaderTitle}>
            Account Details
          </DialogTitle>

          <DialogContent dividers>
            <Grid container>
              {/*<Grid item xs={3}>
                <Typography>
                  Account Status:{' '}
                  {!accountStatus ? 'Loading account details' : accountStatus}
                </Typography>
              </Grid>*/}
              <Grid item xs={6}>
                <Typography>Reference Number: {!selectedCard ? 'Loading card details' : selectedCard.applicationReferenceNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>Account Owner: {!customerData ? 'Loading account details' : customerData.fullname}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>Passport/ID Number: {!customerData ? 'Loading account details' : `${customerData.passport}`} </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography>Product: {!accountData ? 'Loading account details' : accountData.productName} </Typography>
              </Grid>
            </Grid>{' '}
          </DialogContent>

          <DialogTitle id='confirmation-dialog-title' className={classes.dialogHeaderTitle}>
            Card Details
          </DialogTitle>
          <DialogContent dividers>
            <Grid container>
              <Grid item xs={3}>
                <Typography>Cardholder Name: {!customerData ? 'Loading card details' : customerData.fullname}</Typography>
              </Grid>
              {/*<Grid item xs={3}>
                <Typography>
                  Reference Number:{' '}
                  {!selectedCard
                    ? 'Loading card details'
                    : selectedCard.applicationReferenceNumber}
                </Typography>
              </Grid>*/}
              <Grid item xs={3}>
                <Typography>Passport/ID Number: {!customerData ? 'Loading card details' : customerData.passport}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography>Status: {!selectedCard ? 'Loading card details' : selectedCard.status} </Typography>
              </Grid>
            </Grid>{' '}
          </DialogContent>

          <DialogContent dividers>
            <Grid container>
              <Grid item xs={3}>
                <Typography>Card Type: Main Card</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography>Last Four Digits: {!selectedCard ? 'Loading card details' : selectedCard.lastFourDigits}</Typography>
              </Grid>
            </Grid>{' '}
          </DialogContent>
          <DialogContent dividers>
            <Grid container className={classes.actions}>
              {/*<Grid item xs={9}>*/}
              {/*<Typography>
                  Main Cardholder:{' '}
                  {!customerData
                    ? 'Loading card details'
                    : customerData.fullname}
                </Typography>
                <Typography>
                  Card Id:{' '}
                  {!selectedCard ? 'Loading card details' : selectedCard.cardId}
                </Typography>
                <Typography>
                  {' '}
                  Last Four Digits:{' '}
                  {!selectedCard
                    ? 'Loading card details'
                    : selectedCard.lastFourDigits}
                </Typography>
                <Typography>
                  Customer Code:{' '}
                  {!selectedCard
                    ? 'Loading card details'
                    : selectedCard.custCode}
                </Typography>
                <Typography>
                  Card Status:{' '}
                  {!selectedCard ? 'Loading card details' : selectedCard.status}{' '}
                </Typography>*/}
              {/*</Grid>*/}
              {cardsAllowedActionsIndex[selectedCard.cardId][HAS_ROW_ACTION_ACTIVATE] ? (
                <Button
                  className={classes.action}
                  variant='contained'
                  color='primary'
                  style={{ display: 'block', marginBottom: '5px' }}
                  onClick={() => {
                    setSelectedCardId(selectedCard.cardId);
                    setSelectedCardAction('activate');
                    setShowConfirm(true);
                    setDialogResponse(defaultDialogResponse);
                    setActionsDialogOpen(true);
                  }}
                >
                  Activate Card
                </Button>
              ) : null}
              {cardsAllowedActionsIndex[selectedCard.cardId][HAS_ROW_ACTION_REPLACE] && (
                <Button
                  className={classes.action}
                  variant='contained'
                  color='primary'
                  style={{ display: 'block', marginBottom: '5px' }}
                  onClick={() => {
                    setSelectedCardId(selectedCard.cardId);
                    setSelectedCardAction('replace');
                    setShowConfirm(true);
                    setDialogResponse(defaultDialogResponse);
                    setActionsDialogOpen(true);
                  }}
                >
                  Replace Card
                </Button>
              )}
              {cardsAllowedActionsIndex[selectedCard.cardId][HAS_ROW_ACTION_REPRINT] && (
                <Button
                  className={classes.action}
                  variant='contained'
                  color='primary'
                  style={{ display: 'block', marginBottom: '5px' }}
                  onClick={() => {
                    setSelectedCardId(selectedCard.cardId);
                    setSelectedCardAction('reprint');
                    setShowConfirm(true);
                    setDialogResponse(defaultDialogResponse);
                    setActionsDialogOpen(true);
                  }}
                >
                  Reprint Pin
                </Button>
              )}
              {cardsAllowedActionsIndex[selectedCard.cardId][HAS_ROW_ACTION_BLOCK] && (
                <Button
                  className={classes.action}
                  variant='contained'
                  color='primary'
                  style={{ display: 'block', marginBottom: '5px' }}
                  onClick={() => {
                    setSelectedCardId(selectedCard.cardId);
                    setSelectedCardAction(!cardIsBlocked(selectedCard) ? 'block' : 'unblock');
                    setShowConfirm(true);
                    setDialogResponse(defaultDialogResponse);
                    setActionsDialogOpen(true);
                  }}
                >
                  {!cardIsBlocked(selectedCard) ? 'Block Card' : 'Unblock Card'}
                </Button>
              )}
            </Grid>
          </DialogContent>
          <DialogContent dividers>
            <Grid container>
              <Grid item xs={4}>
                <Typography>Address: {!customerData ? 'Loading card details' : customerData.address}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Mobile Number: {!customerData ? 'Loading card details' : '+' + customerData.mobileNumber}</Typography>
                <Typography>Alternate Number: {!customerData ? 'Loading card details' : customerData.alternateMobile}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography>Email: {!customerData ? 'Loading card details' : customerData.email}</Typography>
              </Grid>
            </Grid>{' '}
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Card Actions Dialog */}
      <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={actionsDialogOpen}>
        <DialogContent dividers style={{ textAlign: 'center' }}>
          <Typography>{!dialogResponse ? 'Loading card actions' : dialogResponse}</Typography>
        </DialogContent>
        <DialogActions className={classes.confirmActions}>
          {confirmationLoading ? <CircularProgress className={classes.dialogLoading} size={24} /> : null}
          {showConfirm && (
            <Button autoFocus disabled={confirmationLoading} onClick={callOnConfirm}>
              Confirm
            </Button>
          )}
          <Button autoFocus disabled={confirmationLoading} onClick={() => setActionsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
